import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { rfidLookupUser, rfidManualAttendance, rfidCardScan, rfidPopulateFromIdCards } from "../../../services/rfid";

const ROLE_LABEL = { 4: "Student", 5: "Teacher" };
const roleLabel  = (id) => ROLE_LABEL[id] || "Worker";

const statusBadge = (val) => {
  if (val === "Present") return <span className="badge bg-success">Present</span>;
  if (val === "Absent")  return <span className="badge bg-danger">Absent</span>;
  return <span className="badge bg-secondary">Not marked</span>;
};

export default function ScanCard() {
  const [mode, setMode] = useState("manual"); // 'manual' | 'auto'

  /* ── Manual mode state ── */
  const [searchVal, setSearchVal]   = useState("");
  const [profile, setProfile]       = useState(null);
  const [notFound, setNotFound]     = useState(false);
  const [searching, setSearching]   = useState(false);
  const [actioning, setActioning]   = useState(false);
  const [recentManual, setRecentManual] = useState([]);

  /* ── Auto mode state ── */
  const [cardType, setCardType]   = useState("rfid");
  const [autoUid, setAutoUid]     = useState("");
  const [autoResult, setAutoResult] = useState(null);
  const [recentAuto, setRecentAuto] = useState([]);
  const timerRef  = useRef(null);
  const autoInput = useRef(null);

  /* ── Populate panel ── */
  const [showPopulate, setShowPopulate] = useState(false);
  const [uidField, setUidField]         = useState("qr_code");
  const [popLoading, setPopLoading]     = useState(false);

  const academy = localStorage.getItem("AAcademy") || localStorage.getItem("academy") || 0;

  /* ────────── Manual mode ────────── */
  function handleSearch() {
    const val = searchVal.trim();
    if (!val) return;
    setSearching(true);
    setProfile(null);
    setNotFound(false);
    rfidLookupUser({ uid_or_username: val, academy_id: academy }).then((res) => {
      setSearching(false);
      if (res?.status && res.data.details?.found) {
        setProfile(res.data.details);
      } else {
        setNotFound(true);
      }
    });
  }

  function handleAction(status) {
    if (!profile || actioning) return;
    setActioning(true);
    rfidManualAttendance({
      user_id:    profile.user_id,
      role_id:    profile.role_id,
      status,
      academy_id: academy,
    }).then((res) => {
      setActioning(false);
      if (res?.status) {
        const label = status === 1 ? "Present" : "Absent";
        toast.success(`${profile.name} marked ${label}`);
        setRecentManual((prev) => [{
          name:    profile.name,
          role_id: profile.role_id,
          status:  label,
          time:    moment().format("hh:mm A"),
        }, ...prev].slice(0, 20));
        // Keep profile visible with updated status so it can be changed again
        setProfile((p) => ({ ...p, today_morning: label, today_evening: label }));
        setSearchVal("");
      }
    });
  }

  /* ────────── Auto mode ────────── */
  function handleAutoUidChange(e) {
    const val = e.target.value;
    setAutoUid(val);
    clearTimeout(timerRef.current);
    if (val.trim()) {
      timerRef.current = setTimeout(() => submitAutoScan(val.trim()), 300);
    }
  }

  function submitAutoScan(uid) {
    if (!uid) return;
    rfidCardScan({ card_uid: uid, card_type: cardType, scan_type: "entry", academy_id: academy })
      .then((res) => {
        setAutoUid("");
        autoInput.current?.focus();
        if (!res?.status) return;
        const d = res.data;
        const entry = {
          uid,
          name:       d.user_name || "—",
          role_id:    d.role_id,
          time:       moment().format("hh:mm A"),
          registered: d.registered,
        };
        setAutoResult(entry);
        setRecentAuto((prev) => [entry, ...prev].slice(0, 20));
        if (d.registered) toast.success(`✓ ${d.user_name} — attendance marked`);
        else toast.warning(`Unknown card: ${uid}`);
      });
  }

  /* ────────── Populate ────────── */
  function handlePopulate() {
    setPopLoading(true);
    rfidPopulateFromIdCards({ card_type: cardType, uid_field: uidField, academy_id: academy })
      .then((res) => {
        setPopLoading(false);
        if (res?.status) {
          toast.success(`${res.data.inserted} cards created, ${res.data.skipped} skipped`);
          setShowPopulate(false);
        }
      }).catch(() => setPopLoading(false));
  }

  /* ────────── UI ────────── */
  return (
    <div className="page-header">
      <div className="page-leftheader">
        <h4 className="page-title">RFID / UHF Attendance</h4>
      </div>

      {/* Mode tabs */}
      <div className="col-12 mt-3">
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button className={`nav-link ${mode === "manual" ? "active" : ""}`} onClick={() => setMode("manual")}>
              <i className="fa fa-keyboard-o me-1"></i> Manual Entry
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${mode === "auto" ? "active" : ""}`} onClick={() => { setMode("auto"); setTimeout(() => autoInput.current?.focus(), 100); }}>
              <i className="fa fa-rss me-1"></i> Auto Scan (Device)
            </button>
          </li>
        </ul>

        {/* ══ MANUAL MODE ══ */}
        {mode === "manual" && (
          <div className="row">
            <div className="col-md-5">
              <div className="card">
                <div className="card-body">

                  {/* Search input */}
                  <label className="form-label fw-bold">Enter Username or Scan Card</label>
                  <div className="input-group mb-3">
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      placeholder="Type username or scan card..."
                      value={searchVal}
                      onChange={(e) => setSearchVal(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                      autoFocus
                    />
                    <button className="btn btn-primary" onClick={handleSearch} disabled={searching}>
                      {searching
                        ? <i className="fa fa-spinner fa-spin"></i>
                        : <i className="fa fa-search"></i>}
                    </button>
                  </div>

                  {/* Not found */}
                  {notFound && (
                    <div className="alert alert-warning">
                      <i className="fa fa-exclamation-triangle me-2"></i>
                      No user found for <strong>{searchVal}</strong>
                    </div>
                  )}

                  {/* Profile card */}
                  {profile && (
                    <div className="card border-0 shadow-sm mb-3">
                      <div className="card-body p-0">

                        {/* Header strip */}
                        <div className="d-flex align-items-center gap-3 p-3 border-bottom" style={{ background: "#f8f9fa", borderRadius: "8px 8px 0 0" }}>
                          <div style={{ width: 72, height: 72, borderRadius: "50%", overflow: "hidden", background: "#dee2e6", flexShrink: 0, border: "3px solid #fff", boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}>
                            {profile.photo_url
                              ? <img src={profile.photo_url}
                                     alt="photo" style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                     onError={(e) => { e.target.src = require("../../../assets/images/user.png"); e.target.onerror = null; }} />
                              : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <i className="fa fa-user fa-2x text-secondary"></i>
                                </div>
                            }
                          </div>
                          <div className="flex-grow-1">
                            <h5 className="mb-1 fw-bold">{profile.name}</h5>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <span className="badge bg-primary">{roleLabel(profile.role_id)}</span>
                              {profile.class_name && (
                                <span className="badge bg-light text-dark border">
                                  {profile.class_name}{profile.section_name ? ` / ${profile.section_name}` : ""}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Attendance status row */}
                        <div className="d-flex border-bottom">
                          <div className="flex-fill text-center p-3 border-end">
                            <div className="small text-muted mb-1"><i className="fa fa-sun-o me-1"></i>Morning</div>
                            {statusBadge(profile.today_morning)}
                          </div>
                          <div className="flex-fill text-center p-3">
                            <div className="small text-muted mb-1"><i className="fa fa-moon-o me-1"></i>Evening</div>
                            {statusBadge(profile.today_evening)}
                          </div>
                        </div>

                        {/* Action buttons — same row */}
                        <div className="d-flex gap-2 p-3">
                          <button
                            className="btn btn-success flex-fill"
                            onClick={() => handleAction(1)}
                            disabled={actioning}
                          >
                            {actioning
                              ? <i className="fa fa-spinner fa-spin me-1"></i>
                              : <i className="fa fa-check me-1"></i>}
                            Approve
                          </button>
                          <button
                            className="btn btn-danger flex-fill"
                            onClick={() => handleAction(0)}
                            disabled={actioning}
                          >
                            <i className="fa fa-times me-1"></i>
                            Decline
                          </button>
                        </div>

                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Recent manual entries */}
            <div className="col-md-7">
              <div className="card">
                <div className="card-header"><h6 className="mb-0 text-white">Today's Manual Entries</h6></div>
                <div className="card-body p-0">
                  {recentManual.length === 0
                    ? <p className="text-center text-muted py-4">No entries yet</p>
                    : (
                      <table className="table table-sm table-hover mb-0">
                        <thead className="table-light">
                          <tr><th>#</th><th>Name</th><th>Role</th><th>Status</th><th>Time</th></tr>
                        </thead>
                        <tbody>
                          {recentManual.map((r, i) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>{r.name}</td>
                              <td>{roleLabel(r.role_id)}</td>
                              <td>{statusBadge(r.status)}</td>
                              <td>{r.time}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  }
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ AUTO SCAN MODE ══ */}
        {mode === "auto" && (
          <div className="row">
            <div className="col-md-5">
              <div className="card">
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label">Card Type</label>
                    <select className="form-control" value={cardType} onChange={(e) => setCardType(e.target.value)}>
                      <option value="rfid">RFID</option>
                      <option value="uhf">UHF</option>
                    </select>
                  </div>

                  <label className="form-label fw-bold">
                    Card UID &nbsp;<small className="text-muted fw-normal">(USB reader types here automatically)</small>
                  </label>
                  <input
                    ref={autoInput}
                    type="text"
                    className="form-control form-control-lg mb-2"
                    placeholder="Waiting for card scan..."
                    value={autoUid}
                    onChange={handleAutoUidChange}
                    onKeyDown={(e) => { if (e.key === "Enter") { clearTimeout(timerRef.current); submitAutoScan(autoUid.trim()); }}}
                    autoComplete="off"
                  />
                  <small className="text-muted">
                    Auto-submits 300ms after card tap. Before noon → Morning session; after noon → Evening session.
                  </small>

                  {autoResult && (
                    <div className={`alert mt-3 ${autoResult.registered ? "alert-success" : "alert-warning"}`}>
                      {autoResult.registered
                        ? <><i className="fa fa-check-circle me-2"></i><strong>{autoResult.name}</strong> — attendance marked ({autoResult.time})</>
                        : <><i className="fa fa-exclamation-triangle me-2"></i>Unknown card: <strong>{autoResult.uid}</strong></>
                      }
                    </div>
                  )}

                  <hr />
                  <button className="btn btn-outline-secondary btn-sm w-100" onClick={() => setShowPopulate(!showPopulate)}>
                    <i className="fa fa-database me-1"></i> Pre-populate from existing ID card data
                  </button>

                  {showPopulate && (
                    <div className="mt-3 p-3 border rounded bg-light">
                      <p className="small text-muted mb-2">
                        Creates {cardType.toUpperCase()} card entries for all students/teachers from their existing ID card UID field.
                      </p>
                      <select className="form-control form-control-sm mb-2" value={uidField} onChange={(e) => setUidField(e.target.value)}>
                        <option value="qr_code">QR Code field</option>
                        <option value="barcode">Barcode field</option>
                      </select>
                      <button className="btn btn-warning btn-sm w-100" onClick={handlePopulate} disabled={popLoading}>
                        {popLoading ? <><i className="fa fa-spinner fa-spin me-1"></i>Processing...</> : <><i className="fa fa-bolt me-1"></i>Run Pre-populate</>}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-7">
              <div className="card">
                <div className="card-header"><h6 className="mb-0">Recent Auto Scans (this session)</h6></div>
                <div className="card-body p-0">
                  {recentAuto.length === 0
                    ? <p className="text-center text-muted py-4">No scans yet</p>
                    : (
                      <table className="table table-sm table-hover mb-0">
                        <thead className="table-light">
                          <tr><th>#</th><th>Name</th><th>Role</th><th>Time</th><th>Status</th></tr>
                        </thead>
                        <tbody>
                          {recentAuto.map((s, i) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>{s.name}</td>
                              <td>{roleLabel(s.role_id)}</td>
                              <td>{s.time}</td>
                              <td>{s.registered ? <span className="badge bg-success">OK</span> : <span className="badge bg-warning text-dark">Unknown</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  }
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
